import type { ScaleLinear } from 'd3-scale';
import type { PropsWithChildren } from 'react';
import type { LayoutRectangle } from 'react-native';

export interface Datum {
  x: number;
  y: number;
}

export interface Margin {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export interface Position {
  x: number;
  y: number;
}

export type Domain = [number, number];
export type PartialDomain = [number | undefined, number | undefined];

export interface BaseComponentProps {
  data: Datum[];
  margin: Margin;
  layout?: LayoutRectangle;
  useNiceScaleX?: boolean;
  useNiceScaleY?: boolean;
  /**
   * Used to set the internal starting dimensions of the SVG, providing this as the actual Width
   * on the screen helps ensure text scale is responsive as well as keeping the appropriate
   * resolution across the SVG. Using this along with `useWindowDimensions` is a good starting
   * point for making the graph scale responsively
   */
  baseWidth: number;
  baseHeight: number;
  aspectRatio: number;
  flex?: number;
  xDomain: Domain;
  yDomain: Domain;
  xScale: ScaleLinear<number, number>;
  yScale: ScaleLinear<number, number>;
  xScreenScale: ScaleLinear<number, number>;
  yScreenScale: ScaleLinear<number, number>;
  startTouch?: Position;
  endTouch?: Position;
  overflow?: 'hidden' | 'visible';
}

export type ExternalComponentProps = Partial<BaseComponentProps>;

export interface BasePlotProps extends BaseComponentProps {
  onSelectStart?: (datum: Datum) => void;
  onSelectEnd?: (datum: Datum) => void;
  /**
   * Will only register on Web since Native does not have any concept of hovering
   */
  onHover?: (datum: Datum) => void;
  /**
   * @default 100ms
   */
  onHoverDebounce?: number;
}

export type ExternalPlotProps = PropsWithChildren<Partial<BasePlotProps>>;
